import {
    addDoc,
    collection,
    type DocumentSnapshot,
    deleteDoc,
    doc,
    type FieldValue,
    getCountFromServer,
    getDoc,
    getDocs,
    onSnapshot,
    type QueryConstraint,
    type QuerySnapshot,
    query,
    serverTimestamp,
    Timestamp,
    type UpdateData,
    updateDoc,
} from "firebase/firestore";
import logger from "@/logger";
import { db } from "./firebase";

/**
 * Helper function to convert Firebase Timestamps to JavaScript Dates recursively
 */
function convertTimestampsToDates<T>(data: unknown): T {
    if (data === null || data === undefined) {
        return data as T;
    }

    if (data instanceof Timestamp) {
        return data.toDate() as T;
    }

    if (Array.isArray(data)) {
        return data.map((item) => convertTimestampsToDates(item)) as T;
    }

    if (typeof data === "object") {
        const converted: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(data)) {
            converted[key] = convertTimestampsToDates(value);
        }
        return converted as T;
    }

    return data as T;
}

const firebaseDatabaseService = {
    /**
     * Add a new document
     */
    addDocument: async <T>(collectionPath: string, data: Omit<T, "id">): Promise<string> => {
        try {
            const docRef = await addDoc(collection(db, collectionPath), {
                ...data,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });

            return docRef.id;
        } catch (error) {
            logger.error("Error adding document:", error);
            throw error;
        }
    },

    /**
     * Convert Firebase Timestamps to JavaScript Dates
     */
    convertTimestampsToDates,

    /**
     * Delete a document
     */
    deleteDocument: async (collectionPath: string, documentId: string): Promise<void> => {
        try {
            await deleteDoc(doc(db, collectionPath, documentId));
        } catch (error) {
            logger.error("Error deleting document:", error);
            throw error;
        }
    },

    /**
     * Count documents matching query constraints (Firestore aggregation; does not download documents).
     */
    getCount: async (collectionPath: string, ...constraints: QueryConstraint[]): Promise<number> => {
        try {
            const q = query(collection(db, collectionPath), ...constraints);
            const snapshot = await getCountFromServer(q);
            return snapshot.data().count;
        } catch (error) {
            logger.error("Error counting documents:", error);
            throw error;
        }
    },

    /**
     * Get a single document
     */
    getDocument: async <T>(collectionPath: string, documentId: string): Promise<T | null> => {
        try {
            const docSnap = await getDoc(doc(db, collectionPath, documentId));

            if (!docSnap.exists()) {
                return null;
            }

            const rawData = { id: docSnap.id, ...docSnap.data() };
            return convertTimestampsToDates<T>(rawData);
        } catch (error) {
            logger.error("Error getting document:", error);
            throw error;
        }
    },

    /**
     * Get multiple documents with native Firebase query constraints
     *
     * @example
     * const users = await firebaseDatabaseService.getDocuments<User>(
     *     "users",
     *     where("status", "==", "active"),
     *     orderBy("createdAt", "desc"),
     *     limit(10)
     * );
     */
    getDocuments: async <T>(collectionPath: string, ...constraints: QueryConstraint[]): Promise<T[]> => {
        try {
            const q = query(collection(db, collectionPath), ...constraints);
            const querySnap = await getDocs(q);

            return querySnap.docs.map((docSnap) => {
                const rawData = { id: docSnap.id, ...docSnap.data() };
                return convertTimestampsToDates<T>(rawData);
            });
        } catch (error) {
            logger.error("Error getting documents:", error);
            throw error;
        }
    },

    /**
     * Subscribe to a collection in real-time with native Firebase query constraints
     *
     * @example
     * const unsubscribe = firebaseDatabaseService.subscribeToCollection<User>(
     *     "users",
     *     (users) => console.log(users),
     *     where("status", "==", "active"),
     *     orderBy("createdAt", "desc")
     * );
     */
    subscribeToCollection: <T>(
        collectionPath: string,
        callback: (data: T[]) => void,
        ...constraints: QueryConstraint[]
    ): (() => void) => {
        const q = query(collection(db, collectionPath), ...constraints);

        return onSnapshot(
            q,
            (querySnap: QuerySnapshot) => {
                const data = querySnap.docs.map((docSnap) => {
                    const rawData = { id: docSnap.id, ...docSnap.data() };
                    return convertTimestampsToDates<T>(rawData);
                });
                callback(data);
            },
            (error) => {
                logger.error("Error in collection subscription:", error);
            }
        );
    },

    /**
     * Subscribe to a single document in real-time
     */
    subscribeToDocument: <T>(
        collectionPath: string,
        documentId: string,
        callback: (data: T | null) => void
    ): (() => void) => {
        return onSnapshot(
            doc(db, collectionPath, documentId),
            (docSnap: DocumentSnapshot) => {
                if (!docSnap.exists()) {
                    callback(null);
                    return;
                }

                const rawData = { id: docSnap.id, ...docSnap.data() };
                callback(convertTimestampsToDates<T>(rawData));
            },
            (error) => {
                logger.error("Error in document subscription:", error);
            }
        );
    },

    /**
     * Update an existing document
     */
    updateDocument: async <T>(
        collectionPath: string,
        documentId: string,
        data: UpdateData<T & { updatedAt: FieldValue }>
    ): Promise<void> => {
        try {
            await updateDoc(doc(db, collectionPath, documentId), {
                ...data,
                updatedAt: serverTimestamp(),
            });
        } catch (error) {
            logger.error("Error updating document:", error);
            throw error;
        }
    },
};

export default firebaseDatabaseService;
